import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { fetchProduct, fetchProducts } from "../lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { ArrowRight, CheckCircle2, FileText } from "lucide-react";
import InquiryDialog from "../components/InquiryDialog";
import ProductCard from "../components/ProductCard";
import Seo from "../components/Seo";
import { DETAIL } from "../constants/testIds";
import {
  buildBreadcrumbSchema,
  buildCanonicalUrl,
  buildOrganizationSchema,
  buildProductSchema,
  buildWebsiteSchema,
  toAbsoluteUrl,
  truncateDescription,
} from "../lib/seo";

export default function ProductDetail() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [activeImg, setActiveImg] = useState(0);
  const [inqOpen, setInqOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    setActiveImg(0);
    fetchProduct(slug)
      .then((p) => {
        setProduct(p);
        return fetchProducts({ category: p.category_slug, limit: 4 });
      })
      .then((rel) => setRelated((rel || []).filter((r) => r.slug !== slug).slice(0, 3)))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading || !product) {
    return (
      <>
        <Seo
          title="Industrial Machine Specifications | Buildwit Technology"
          description="Explore Buildwit Technology's industrial machinery specifications, applications and engineering details."
          canonicalPath={`/products/${slug}`}
          type="website"
          schema={[
            buildOrganizationSchema(),
            buildWebsiteSchema(),
            buildBreadcrumbSchema(`/products/${slug}`, "Product details"),
          ]}
        />
        <div className="section-x mx-auto max-w-[1440px] py-32 text-center text-slate-500">Loading machine...</div>
      </>
    );
  }

  const productTitle = `${product.name} | Industrial Machinery by Buildwit Technology`;
  const productDescription = truncateDescription(
    [product.tagline, product.description].filter(Boolean).join(" "),
    160
  );
  const productCanonicalPath = `/products/${product.slug}`;
  const productImage = Array.isArray(product.images) && product.images.length > 0 ? toAbsoluteUrl(product.images[0]) : undefined;
  const productKeywords = [
    product.name,
    product.category_slug?.replace(/-/g, " "),
    ...(Array.isArray(product.industries) ? product.industries : []),
    "industrial machinery manufacturer India",
    "Buildwit Technology",
  ].filter(Boolean);
  const productPageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: productTitle,
    description: productDescription,
    url: buildCanonicalUrl(productCanonicalPath),
    isPartOf: {
      "@id": `${buildCanonicalUrl("/")}#website`,
    },
    about: {
      "@id": `${buildCanonicalUrl("/")}#organization`,
    },
  };

  return (
    <>
      <Seo
        title={productTitle}
        description={productDescription}
        canonicalPath={productCanonicalPath}
        image={productImage}
        type="product"
        keywords={productKeywords}
        schema={[
          buildOrganizationSchema(),
          buildWebsiteSchema(),
          buildBreadcrumbSchema(productCanonicalPath, product.name),
          productPageSchema,
          buildProductSchema(product),
        ]}
      />

      <div data-testid={DETAIL.root} className="bg-slate-50 border-b border-slate-200">
        <div className="section-x mx-auto max-w-[1440px] py-3 text-xs text-slate-500 flex items-center gap-2">
          <Link to="/" className="hover:text-brand-navy">Home</Link><span>/</span>
          <Link to="/products" className="hover:text-brand-navy">Products</Link><span>/</span>
          <span className="text-brand-navy font-semibold">{product.name}</span>
        </div>
      </div>

      <section className="section-x mx-auto max-w-[1440px] py-14 grid lg:grid-cols-12 gap-12">
        {/* Gallery */}
        <div className="lg:col-span-7">
          <div className="relative rounded-2xl overflow-hidden border border-slate-200 bg-white aspect-[4/3]">
            <img data-testid={DETAIL.mainImg} src={product.images[activeImg]} alt={product.name} className="w-full h-full object-cover"/>
            <span className="corner-mark tl"/><span className="corner-mark tr"/><span className="corner-mark bl"/><span className="corner-mark br"/>
          </div>
          <div className="mt-4 grid grid-cols-4 gap-3">
            {product.images.map((im, i) => (
              <button
                key={i}
                data-testid={DETAIL.thumb(i)}
                onClick={() => setActiveImg(i)}
                className={`relative aspect-[4/3] rounded-lg overflow-hidden border transition-colors duration-200 ${activeImg === i ? "border-accent" : "border-slate-200 hover:border-slate-400"}`}
              >
                <img src={im} alt={`view ${i + 1}`} className="w-full h-full object-cover"/>
              </button>
            ))}
          </div>
        </div>

        {/* Info */}
        <div className="lg:col-span-5 lg:sticky lg:top-[92px] lg:self-start">
          <Badge className="bg-brand-navy/5 text-brand-navy border border-brand-navy/10 uppercase tracking-widest text-[10px] font-bold hover:bg-brand-navy/10">{product.category_slug.replace(/-/g, " ")}</Badge>
          <h1 data-testid={DETAIL.title} className="mt-4 font-display text-4xl md:text-5xl font-black text-brand-navy tracking-tight leading-tight">{product.name}</h1>
          <p className="mt-4 text-slate-600 text-lg leading-relaxed">{product.tagline}</p>

          <div className="mt-8 flex flex-wrap gap-2">
            {product.industries.map((ind) => (
              <span key={ind} className="text-[11px] font-semibold uppercase tracking-widest text-slate-500 border border-slate-200 rounded-full px-3 py-1">{ind}</span>
            ))}
          </div>

          <div className="mt-8 flex items-center gap-3">
            <button
              onClick={() => setInqOpen(true)}
              data-testid={DETAIL.inquireBtn}
              className="inline-flex items-center gap-2 h-12 px-6 rounded-md bg-accent hover:bg-accent-hover text-white text-sm font-semibold shadow-sm hover:-translate-y-0.5 transition-[transform,background-color] duration-200"
            >
              Request Quote <ArrowRight className="h-4 w-4"/>
            </button>
            <a href="#specs" className="inline-flex items-center gap-2 h-12 px-6 rounded-md border border-slate-200 text-brand-navy text-sm font-semibold hover:border-brand-navy transition-colors duration-200">
              <FileText className="h-4 w-4"/> Full specs
            </a>
          </div>

          {product.price_from && (
            <div className="mt-6 text-xs uppercase tracking-widest text-slate-500">
              Price <span className="ml-2 text-brand-navy font-bold text-sm">{product.price_from}</span>
            </div>
          )}
        </div>
      </section>

      {/* Tabs */}
      <section id="specs" className="section-x mx-auto max-w-[1440px] pb-24">
        <Tabs defaultValue="details">
          <TabsList className="bg-white border border-slate-200 rounded-md p-1">
            <TabsTrigger value="details" data-testid={DETAIL.tabDetails} className="data-[state=active]:bg-brand-navy data-[state=active]:text-white text-sm font-semibold">Overview</TabsTrigger>
            <TabsTrigger value="specs" data-testid={DETAIL.tabSpecs} className="data-[state=active]:bg-brand-navy data-[state=active]:text-white text-sm font-semibold">Specifications</TabsTrigger>
            <TabsTrigger value="apps" data-testid={DETAIL.tabApps} className="data-[state=active]:bg-brand-navy data-[state=active]:text-white text-sm font-semibold">Applications</TabsTrigger>
          </TabsList>

          <TabsContent value="details" className="mt-8 grid md:grid-cols-2 gap-10">
            <div>
              <h3 className="font-display text-2xl font-extrabold text-brand-navy tracking-tight">About this machine</h3>
              <p className="mt-4 text-slate-600 text-base leading-relaxed">{product.description}</p>
            </div>
            <div>
              <h3 className="font-display text-2xl font-extrabold text-brand-navy tracking-tight">Key advantages</h3>
              <ul className="mt-4 space-y-3">
                {product.advantages.map((a, i) => (
                  <li key={i} className="flex items-start gap-3 text-slate-700"><CheckCircle2 className="h-5 w-5 text-accent mt-0.5 shrink-0"/> {a}</li>
                ))}
              </ul>
            </div>
          </TabsContent>

          <TabsContent value="specs" className="mt-8">
            <div className="rounded-xl border border-slate-200 overflow-hidden max-w-3xl">
              {product.specifications.map((s, i) => (
                <div key={i} className="spec-row grid grid-cols-2 border-b last:border-b-0 border-slate-100">
                  <div className="px-6 py-4 text-sm text-slate-500 font-medium">{s.key}</div>
                  <div className="px-6 py-4 text-sm text-brand-navy font-semibold">{s.value}</div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="apps" className="mt-8">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {product.applications.map((a, i) => (
                <div key={i} className="rounded-xl border border-slate-200 bg-white p-6">
                  <div className="text-accent font-display text-3xl font-black">{String(i + 1).padStart(2, "0")}</div>
                  <div className="mt-2 font-semibold text-brand-navy">{a}</div>
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>

        {Array.isArray(product.detail_images) && product.detail_images.length > 0 ? (
          <div className="mt-14">
            <div className="overline text-accent">Product detail charts</div>
            <h3 className="mt-2 font-display text-2xl font-extrabold tracking-tight text-brand-navy">Technical charts and detailed visuals</h3>
            <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {product.detail_images.map((image, index) => (
                <div key={`${product.slug}-detail-${index}`} className="overflow-hidden rounded-xl border border-slate-200 bg-white">
                  <img src={image} alt={`${product.name} chart ${index + 1}`} className="h-full w-full object-cover" />
                </div>
              ))}
            </div>
          </div>
        ) : null}
      </section>

      {related.length > 0 && (
        <section className="bg-slate-50 border-t border-slate-200">
          <div className="section-x mx-auto max-w-[1440px] py-20">
            <div className="overline text-accent">Related machinery</div>
            <h2 className="mt-3 font-display text-3xl md:text-4xl font-extrabold text-brand-navy tracking-tight">More from {product.category_slug.replace(/-/g, " ")}</h2>
            <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
              {related.map((r, i) => <ProductCard key={r.slug} product={r} index={i}/>)}
            </div>
          </div>
        </section>
      )}

      <InquiryDialog open={inqOpen} onOpenChange={setInqOpen} product={product}/>
    </>
  );
}
